import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Button, Layer, ResponsiveContext, Text } from "grommet";
import { useSearchParams } from "react-router-dom";
import CraneLinkModal from "../Components/PodV2/CraneLinkModal";
import PodAgentAlertsCard from "../Components/PodV2/PodAgentAlertsCard";
import PodNotesCard from "../Components/PodV2/PodNotesCard";
import PodSessionHistoryModal from "../Components/PodV2/PodSessionHistoryModal";
import PodSessionInfoCard from "../Components/PodV2/PodSessionInfoCard";
import { SavedToast, useSavedToast } from "../Components/PodV2/SavedToast";
import { formatAgentAlertSummary } from "../Components/PodV2/agentAlertsUtils";
import { parsePodSessionNotes } from "../Components/PodV2/notesUtils";
import { useGoogleMeet } from "../Components/PodV2/useGoogleMeet";
import { usePodSessionData } from "../Components/PodV2/usePodSessionData";
import {
  fetchCraneGuageSession,
  fetchPodSessionHistory,
  listAllCraneGuageSessions,
  syncPodMasterSheetToGoogle,
  updateCraneGuageSessionMeetLink,
} from "../Components/PodV2/api";
import {
  getProctorUsername,
  isClaimableProctor,
  isFinishedSessionNotesLocked,
} from "../Components/PodV2/sessionUtils";

const bannerMessages = [
  "REMINDER: Orange Flag is now active.",
  "Fields autosave when you leave them. Use Save Notes anytime as a backup.",
];

export default function PodSessionv2() {
  const size = useContext(ResponsiveContext);
  const isNarrow = size === "small";
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("id");
  const { showSaved, savedToastProps } = useSavedToast();

  const {
    podSession,
    organization,
    podAgentAlerts,
    podExamCodes,
    podNotesFormat,
    user,
    userEmail,
    loading,
    claiming,
    claimSession,
    unclaimSession,
    updatePodSession,
    setTesterHasJoined,
  } = usePodSessionData(sessionId, {
    onSaved: showSaved,
    onClaimNotice: showSaved,
  });

  const {
    localMeetLink,
    setLocalMeetLink,
    meetLoading,
    meetLinkLoading,
    handleGenerateGoogleMeetLink,
    handleSaveMeetLink,
  } = useGoogleMeet({
    podSession,
    updatePodSession,
  });

  const [agentAlertExamOverride, setAgentAlertExamOverride] = useState();
  const [agentAlertInstructorOverride, setAgentAlertInstructorOverride] =
    useState();
  const [idCheckOverride, setIdCheckOverride] = useState();
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [craneModalOpen, setCraneModalOpen] = useState(false);
  const [craneSession, setCraneSession] = useState(null);
  const [craneSessionLoading, setCraneSessionLoading] = useState(false);
  const [craneSessionError, setCraneSessionError] = useState(null);
  const cranePromptedIdRef = useRef(null);
  const loadedSessionId = podSession?.id;
  const loadedOrganizationId = podSession?.organizationId;
  const loadedIntegrationID = podSession?.integrationID;
  const loadedProctor = podSession?.proctor;
  const loadSessionHistory = useCallback(() => {
    if (!sessionId) return Promise.resolve({ items: [], truncated: false });
    return fetchPodSessionHistory(sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (podSession?.testerName && podSession?.organizationName) {
      const originalTitle = document.title;
      document.title = `${podSession.testerName} | ${podSession.organizationName}`;

      return () => {
        document.title = originalTitle;
      };
    }
  }, [podSession?.testerName, podSession?.organizationName]);

  useEffect(() => {
    setAgentAlertExamOverride(undefined);
    setAgentAlertInstructorOverride(undefined);
    setIdCheckOverride(undefined);
    setHistoryOpen(false);
  }, [sessionId]);

  useEffect(() => {
    if (!loadedSessionId) return;
    setShowClaimModal(isClaimableProctor(loadedProctor));
  }, [loadedProctor, loadedSessionId]);

  useEffect(() => {
    cranePromptedIdRef.current = null;
    setCraneModalOpen(false);
    setCraneSession(null);
    setCraneSessionError(null);
  }, [sessionId]);

  useEffect(() => {
    if (!podSession || podSession.id !== sessionId) return;
    if (podSession.organizationId !== "crane") return;
    if (String(podSession.integrationID ?? "").trim()) return;
    if (isClaimableProctor(podSession.proctor)) return;
    if (cranePromptedIdRef.current === podSession.id) return;

    cranePromptedIdRef.current = podSession.id;
    setCraneModalOpen(true);
  }, [podSession, sessionId]);

  useEffect(() => {
    if (!loadedSessionId || loadedSessionId !== sessionId) return undefined;
    if (loadedOrganizationId !== "crane") {
      setCraneSession(null);
      setCraneSessionError(null);
      return undefined;
    }

    const integrationID = String(loadedIntegrationID ?? "").trim();
    if (!integrationID) {
      setCraneSession(null);
      setCraneSessionError(null);
      return undefined;
    }

    let cancelled = false;
    setCraneSessionLoading(true);
    setCraneSessionError(null);

    fetchCraneGuageSession(integrationID)
      .then((session) => {
        if (!cancelled) setCraneSession(session);
      })
      .catch((error) => {
        console.error(error);
        if (cancelled) return;
        setCraneSession(null);
        setCraneSessionError("Unable to load the linked Crane session.");
      })
      .finally(() => {
        if (!cancelled) setCraneSessionLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadedIntegrationID, loadedOrganizationId, loadedSessionId, sessionId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!podSession) {
    return <div>Pod session not found</div>;
  }

  const currentUsername = getProctorUsername(user, userEmail);
  const isUnclaimed = isClaimableProctor(podSession?.proctor);

  const isCrane = podSession.organizationId === "crane";
  const craneLinked = Boolean(String(podSession.integrationID ?? "").trim());

  const handleLinkCraneSession = async (session) => {
    const meetLink = String(podSession.googleMeetLink ?? "").trim();
    if (meetLink) {
      await updateCraneGuageSessionMeetLink(session.session_id, meetLink);
    }
    await updatePodSession({ integrationID: session.session_id });
    setCraneModalOpen(false);
  };

  const handleSaveNotes = async (notes, options = {}) => {
    return await updatePodSession({ notes, ...options });
  };

  const handleSyncMasterSheet = async (updatedPodSession) => {
    return await syncPodMasterSheetToGoogle(updatedPodSession);
  };

  const handleTransferToProctor = async (username) => {
    await updatePodSession({ transferToProctor: username });
  };

  const handleVerifyIdentity = async () => {
    if (!userEmail) return;
    await updatePodSession({ verified_by: userEmail });
    setIdCheckOverride(true);
  };

  const handleAgentAlertSelectionChange = (option) => {
    if (podSession?.organizationId !== "cdle") {
      setAgentAlertExamOverride(
        option.isDefault ? "" : formatAgentAlertSummary(option),
      );
    }
    setAgentAlertInstructorOverride(
      option.isDefault ? "" : option.instructor || "",
    );
  };

  const sessionExamValue = parsePodSessionNotes(
    podSession?.notes,
    podSession?.fields,
  ).exam;

  return (
    <Box fill pad="medium" gap="medium">
      {showClaimModal && (
        <Layer modal onEsc={false}>
          <Box pad="large" gap="medium" width="medium" align="center">
            <Text weight="bold" size="large" textAlign="center">
              Claim Session Required
            </Text>
            <Text textAlign="center">
              This session has not been claimed yet. Please claim it before
              continuing.
            </Text>
            <Button
              label="Claim Session"
              primary
              color="black"
              onClick={claimSession}
              disabled={claiming || !userEmail}
            />
            {claiming && <Text>Claiming...</Text>}
            {!userEmail && (
              <Text size="small" color="status-critical">
                Waiting for user authentication...
              </Text>
            )}
          </Box>
        </Layer>
      )}
      <Box direction="row" justify="end">
        <Button label="View History" onClick={() => setHistoryOpen(true)} />
      </Box>

      <Box gap="xsmall">
        {bannerMessages.map((message) => (
          <Box
            key={message}
            background="#FFD08A"
            pad={{ horizontal: "medium", vertical: "xsmall" }}
            round="small"
          >
            <Text size="small" textAlign="center">
              {message}
            </Text>
          </Box>
        ))}
      </Box>

      <Box direction={isNarrow ? "column" : "row"} gap="medium">
        <Box basis={isNarrow ? "full" : "1/2"} fill>
          <PodSessionInfoCard
            podSession={podSession}
            organization={organization}
            podExamCodes={podExamCodes}
            podNotesFormat={podNotesFormat}
            userEmail={userEmail}
            claiming={claiming}
            onClaim={claimSession}
            onUnclaim={unclaimSession}
            localMeetLink={localMeetLink}
            setLocalMeetLink={setLocalMeetLink}
            meetLinkLoading={meetLinkLoading}
            onSaveMeetLink={handleSaveMeetLink}
            meetLoading={meetLoading}
            onGenerateGoogleMeetLink={handleGenerateGoogleMeetLink}
            onTransferToProctor={handleTransferToProctor}
            onVerifyIdentity={handleVerifyIdentity}
            onUpdateSessionInfo={updatePodSession}
            testerJoinedDisabled={
              isUnclaimed || isFinishedSessionNotesLocked(podSession)
            }
            onTesterJoinedChange={setTesterHasJoined}
          />
        </Box>
        <Box basis={isNarrow ? "full" : "1/2"} fill>
          <PodAgentAlertsCard
            organization={organization}
            podAgentAlerts={podAgentAlerts}
            examValue={sessionExamValue}
            onSelectionChange={handleAgentAlertSelectionChange}
            craneSession={isCrane ? craneSession : null}
            craneSessionLoading={isCrane && craneSessionLoading}
            craneSessionError={isCrane ? craneSessionError : null}
            craneLinked={isCrane && craneLinked}
            onLinkCraneSession={
              isCrane ? () => setCraneModalOpen(true) : undefined
            }
          />
        </Box>
      </Box>

      <PodNotesCard
        podSession={podSession}
        podNotesFormat={podNotesFormat}
        agentAlertExamOverride={agentAlertExamOverride}
        agentAlertInstructorOverride={agentAlertInstructorOverride}
        idCheckOverride={idCheckOverride}
        onSave={handleSaveNotes}
        onSyncMasterSheet={handleSyncMasterSheet}
        currentUsername={currentUsername}
        user={user}
        userEmail={userEmail}
        isUnclaimed={isUnclaimed}
      />

      {historyOpen && (
        <PodSessionHistoryModal
          onClose={() => setHistoryOpen(false)}
          loadHistory={loadSessionHistory}
        />
      )}

      {craneModalOpen && (
        <CraneLinkModal
          onClose={() => setCraneModalOpen(false)}
          onLink={handleLinkCraneSession}
          loadSessions={listAllCraneGuageSessions}
        />
      )}

      <SavedToast {...savedToastProps} />
    </Box>
  );
}
